import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { CheckSquare, Archive, ArchiveRestore, Lock, ChevronUp, ChevronDown, ChevronsUpDown, Filter, X } from 'lucide-react';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { useParadigm } from '../../contexts/ParadigmContext';
import TripDetails2 from './TripDetails2';
import CreateTripModal2 from './CreateTripModal2';

// Status color scheme matching executive dashboard
const statusColors = {
  draft: "bg-gray-100 text-gray-800 border-gray-300",
  planning: "bg-blue-100 text-blue-800 border-blue-300",
  confirmed: "bg-green-100 text-green-800 border-green-300",
  booked: "bg-blue-100 text-blue-800 border-blue-300",
  in_progress: "bg-purple-100 text-purple-800 border-purple-300",
  completed: "bg-slate-100 text-slate-800 border-slate-300",
  cancelled: "bg-red-100 text-red-800 border-red-300"
};

// Purpose/type color scheme matching executive dashboard
const purposeColors = {
  board_meeting: "bg-purple-100 text-purple-800 border-purple-300",
  client_meeting: "bg-blue-100 text-blue-800 border-blue-300",
  conference: "bg-indigo-100 text-indigo-800 border-indigo-300",
  site_visit: "bg-green-100 text-green-800 border-green-300",
  investor_meeting: "bg-amber-100 text-amber-800 border-amber-300",
  speaking_engagement: "bg-pink-100 text-pink-800 border-pink-300",
  team_meeting: "bg-cyan-100 text-cyan-800 border-cyan-300",
  personal: "bg-gray-100 text-gray-800 border-gray-300",
  other: "bg-slate-100 text-slate-800 border-slate-300"
};

function getStatusColor(status) {
  const normalizedStatus = (status || 'planning').toLowerCase();
  return statusColors[normalizedStatus] || statusColors.draft;
}

// Helper function to check if a date string is valid
function isValidDate(dateString) {
  if (!dateString || dateString === 'null' || dateString === 'undefined') return false;
  try {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  } catch {
    return false;
  }
}

// Status order for sorting
const STATUS_ORDER = ['planning', 'confirmed', 'booked', 'in_progress', 'completed', 'cancelled', 'draft'];

// Sort comparator factory
function makeSortComparator(sortKey, sortDirection, getCreatorName, getPeopleNames) {
  const dir = sortDirection === 'asc' ? 1 : -1;

  return (a, b) => {
    let cmp = 0;
    switch (sortKey) {
      case 'title':
        cmp = (a.title || '').localeCompare(b.title || '');
        break;
      case 'dates': {
        const aHas = isValidDate(a.start_date);
        const bHas = isValidDate(b.start_date);
        if (aHas && bHas) cmp = new Date(a.start_date) - new Date(b.start_date);
        else if (aHas) cmp = -1;
        else if (bHas) cmp = 1;
        else cmp = (a.title || '').localeCompare(b.title || '');
        break;
      }
      case 'executives': {
        const aExec = getPeopleNames(a).join(', ').toLowerCase();
        const bExec = getPeopleNames(b).join(', ').toLowerCase();
        cmp = aExec.localeCompare(bExec);
        break;
      }
      case 'created_by': {
        const aName = getCreatorName(a.created_by).toLowerCase();
        const bName = getCreatorName(b.created_by).toLowerCase();
        cmp = aName.localeCompare(bName);
        break;
      }
      case 'status': {
        const aIdx = STATUS_ORDER.indexOf((a.status || 'planning').toLowerCase());
        const bIdx = STATUS_ORDER.indexOf((b.status || 'planning').toLowerCase());
        cmp = aIdx - bIdx;
        break;
      }
      default: {
        // Default: dates
        const aHas = isValidDate(a.start_date);
        const bHas = isValidDate(b.start_date);
        if (aHas && bHas) cmp = new Date(a.start_date) - new Date(b.start_date);
        else if (aHas) cmp = -1;
        else if (bHas) cmp = 1;
        else cmp = (a.title || '').localeCompare(b.title || '');
      }
    }
    return cmp * dir;
  };
}

const TripManagement = ({ onOpenTrip, onCreateTrip: onCreateTripProp }) => {
  const { labels, isSpeakerBureau } = useParadigm();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // Default to table view
  const [users, setUsers] = useState([]);
  const [todos, setTodos] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [executives, setExecutives] = useState([]);
  const [teams, setTeams] = useState([]);
  const [sortKey, setSortKey] = useState('dates');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedExecutives, setSelectedExecutives] = useState([]);
  const [showExecutiveFilter, setShowExecutiveFilter] = useState(false);
  const executiveFilterRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const selectedTripId = searchParams.get('id');

  // Guide deep link: open trip creation immediately and consume the action so
  // closing or refreshing the page does not reopen the modal.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('action') !== 'new-trip') return;

    setShowCreateModal(true);
    params.delete('action');
    navigate(
      { pathname: location.pathname, search: params.toString() ? `?${params}` : '' },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  const fetchTrips = useCallback(async (includeArchived = false) => {
    const apiBase = getApiBase();
    const headers = getAuthHeaders();
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${apiBase}/trips?include_archived=${includeArchived}`, { headers });
      const items = Array.isArray(res.data) ? res.data : [];
      setTrips(items);
    } catch (e) {
      console.error('Failed to load trips', e);
      setError('Failed to load trips');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    const apiBase = getApiBase();
    const headers = getAuthHeaders();
    try {
      const res = await axios.get(`${apiBase}/users`, { headers });
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load users', e);
    }
  }, []);

  const fetchTodos = useCallback(async () => {
    const apiBase = getApiBase();
    const headers = getAuthHeaders();
    try {
      const res = await axios.get(`${apiBase}/todos`, { headers });
      setTodos(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load todos', e);
    }
  }, []);

  const fetchExecutives = useCallback(async () => {
    const apiBase = getApiBase();
    const headers = getAuthHeaders();
    try {
      const res = await axios.get(`${apiBase}/executives`, { headers });
      setExecutives(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load executives', e);
    }
  }, []);

  const fetchTeams = useCallback(async () => {
    const apiBase = getApiBase();
    const headers = getAuthHeaders();
    try {
      const res = await axios.get(`${apiBase}/teams`, { headers });
      // API returns {success: true, teams: [...], ...}
      const teamsData = res.data?.teams || res.data;
      setTeams(Array.isArray(teamsData) ? teamsData : []);
    } catch (e) {
      console.error('Failed to load teams', e);
    }
  }, []);

  useEffect(() => {
    fetchTrips(showArchived);
    fetchUsers();
    fetchTodos();
    fetchExecutives();
    fetchTeams();
  }, [fetchTrips, fetchUsers, fetchTodos, fetchExecutives, fetchTeams, showArchived]);

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  const getCreatorName = useCallback((createdById) => {
    if (!createdById) return 'Unknown';
    const user = users.find(u => u.id === createdById);
    if (user) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Unknown';
    }
    return 'Unknown';
  }, [users]);

  // Get all users who have access to a trip (via direct collaboration or team membership)
  const getUsersWithAccess = (trip) => {
    const accessUsers = [];
    const addedUserIds = new Set();
    const creatorId = trip.created_by;

    // Helper to get user name
    const getUserName = (userId) => {
      const user = users.find(u => u.id === userId);
      if (user) {
        return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Unknown';
      }
      return null;
    };

    // Add direct collaborators
    const collaborators = trip.collaborators || [];
    for (const collab of collaborators) {
      if (collab.user_id && !addedUserIds.has(collab.user_id)) {
        const name = getUserName(collab.user_id);
        if (name) {
          accessUsers.push({ id: collab.user_id, name, accessType: 'Collaborator' });
          addedUserIds.add(collab.user_id);
        }
      }
    }

    // Find team members of the creator (they have full_admin access)
    if (creatorId) {
      for (const team of teams) {
        if (!team.is_active) continue;
        const teamMembers = team.members || [];
        const creatorInTeam = teamMembers.some(m => m.user_id === creatorId);
        if (creatorInTeam) {
          // Add all other team members
          for (const member of teamMembers) {
            if (member.user_id && member.user_id !== creatorId && !addedUserIds.has(member.user_id)) {
              const name = getUserName(member.user_id);
              if (name) {
                accessUsers.push({ id: member.user_id, name, accessType: `Team: ${team.name}` });
                addedUserIds.add(member.user_id);
              }
            }
          }
        }
      }
    }

    return accessUsers;
  };

  const getTodoCountForTrip = (tripId) => {
    return todos.filter(todo => todo.trip_id === tripId && todo.status !== 'completed').length;
  };

  const formatDate = (dateString) => {
    // Return empty string for missing or invalid dates
    if (!dateString || dateString === 'null' || dateString === 'undefined') return '';

    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return '';

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '';
    }
  };

  const onCreateTrip = () => {
    setShowCreateModal(true);
  };

  const handleTripCreated = async (newTrip) => {
    // Refresh trips list
    await fetchTrips();
    // Call parent callback if provided
    if (typeof onCreateTripProp === 'function') {
      onCreateTripProp(newTrip);
    }
    // Navigate to the new trip so user can start adding components
    if (newTrip?.id) {
      navigate(`/trips?id=${newTrip.id}`);
    }
  };

  const onDeleteTrip = async (tripId) => {
    const apiBase = getApiBase();
    const headers = getAuthHeaders();
    if (!tripId) return;
    if (!window.confirm('Delete this trip?')) return;
    try {
      await axios.delete(`${apiBase}/trips/${tripId}`, { headers });
      await fetchTrips(showArchived);
    } catch (e) {
      alert('Failed to delete trip');
    }
  };

  const onArchiveTrip = async (tripId, isCurrentlyArchived) => {
    const apiBase = getApiBase();
    const headers = getAuthHeaders();
    if (!tripId) return;
    try {
      const endpoint = isCurrentlyArchived ? 'unarchive' : 'archive';
      await axios.post(`${apiBase}/trips/${tripId}/${endpoint}`, {}, { headers });
      await fetchTrips(showArchived);
    } catch (e) {
      console.error('Failed to archive/unarchive trip', e);
      alert(`Failed to ${isCurrentlyArchived ? 'unarchive' : 'archive'} trip`);
    }
  };

  // Close executive filter on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (executiveFilterRef.current && !executiveFilterRef.current.contains(e.target)) {
        setShowExecutiveFilter(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const toggleExecutiveFilter = (execName) => {
    setSelectedExecutives(prev =>
      prev.includes(execName)
        ? prev.filter(n => n !== execName)
        : [...prev, execName]
    );
  };

  // Speaker programs carry their people in speaker_names; executive trips in
  // executive_names. Older speaker-org trips may still only have
  // executive_names, so fall back to it.
  const getTripPeopleNames = useCallback((trip) => {
    if (isSpeakerBureau) {
      const speakers = trip.speaker_names || [];
      return speakers.length > 0 ? speakers : (trip.executive_names || []);
    }
    return trip.executive_names || [];
  }, [isSpeakerBureau]);

  // Build unique executive/speaker names from all trips for the filter dropdown
  const allExecutiveNames = useMemo(() => {
    const names = new Set();
    trips.forEach(trip => {
      getTripPeopleNames(trip).forEach(name => names.add(name));
    });
    return [...names].sort();
  }, [trips, getTripPeopleNames]);

  // Filter and sort trips
  const sortedTrips = useMemo(() => {
    let filtered = trips;
    if (selectedExecutives.length > 0) {
      filtered = trips.filter(trip =>
        getTripPeopleNames(trip).some(name => selectedExecutives.includes(name))
      );
    }
    return [...filtered].sort(makeSortComparator(sortKey, sortDirection, getCreatorName, getTripPeopleNames));
  }, [trips, selectedExecutives, sortKey, sortDirection, getCreatorName, getTripPeopleNames]);

  // If a specific trip is requested via query param, render TripDetails2 (compact header design)
  if (selectedTripId) {
    return <TripDetails2 tripId={selectedTripId} />;
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800">Trips</h2>
          <p className="text-slate-600 text-sm sm:text-base">Manage and organize your trips</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Show Archived Toggle */}
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="hidden sm:inline">Show archived</span>
            <Archive className="w-4 h-4 sm:hidden" />
          </label>
          {/* Executive Filter */}
          <div className="relative" ref={executiveFilterRef}>
            <button
              onClick={() => setShowExecutiveFilter(!showExecutiveFilter)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                selectedExecutives.length > 0
                  ? 'bg-indigo-50 border-indigo-300 text-indigo-700'
                  : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">
                {selectedExecutives.length > 0 ? `${labels.executives} (${selectedExecutives.length})` : labels.executives}
              </span>
            </button>
            {showExecutiveFilter && (
              <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-lg min-w-[220px] max-h-72 overflow-auto">
                {selectedExecutives.length > 0 && (
                  <button
                    onClick={() => setSelectedExecutives([])}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 border-b border-slate-100"
                  >
                    <X className="w-3.5 h-3.5" />
                    Clear filters
                  </button>
                )}
                {allExecutiveNames.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-slate-500">No {labels.executives.toLowerCase()} found</div>
                ) : (
                  allExecutiveNames.map(name => (
                    <label
                      key={name}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedExecutives.includes(name)}
                        onChange={() => toggleExecutiveFilter(name)}
                        className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      {name}
                    </label>
                  ))
                )}
              </div>
            )}
          </div>
          {/* View Mode Toggle */}
          <div className="flex items-center bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => handleViewModeChange('table')}
              className={`px-2.5 sm:px-3 py-1.5 rounded text-sm font-medium transition-colors touch-target ${
                viewMode === 'table'
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              <svg className="w-4 h-4 inline-block sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span className="hidden sm:inline">Table</span>
            </button>
            <button
              onClick={() => handleViewModeChange('icon')}
              className={`px-2.5 sm:px-3 py-1.5 rounded text-sm font-medium transition-colors touch-target ${
                viewMode === 'icon'
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              <svg className="w-4 h-4 inline-block sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              <span className="hidden sm:inline">Cards</span>
            </button>
          </div>
          <button
            onClick={onCreateTrip}
            className="flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-3 sm:px-4 py-2 rounded-lg transition-colors touch-target"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden sm:inline">New Trip</span>
          </button>
        </div>
      </div>

      {selectedExecutives.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2 mb-4">
          <span className="text-sm text-indigo-700 font-medium">Filtered by:</span>
          {selectedExecutives.map(name => (
            <span key={name} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-800">
              {name}
              <button onClick={() => toggleExecutiveFilter(name)} className="hover:text-indigo-600">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <button onClick={() => setSelectedExecutives([])} className="text-xs text-indigo-600 hover:text-indigo-800 ml-auto">
            Clear all
          </button>
          <span className="text-xs text-indigo-500">{sortedTrips.length} trip{sortedTrips.length !== 1 ? 's' : ''}</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="text-red-700">{error}</div>
        </div>
      )}

      {loading && (
        <div className="text-center py-8">
          <div className="text-slate-500">Loading trips...</div>
        </div>
      )}

      {/* Table View */}
      {!loading && viewMode === 'table' && (
        <>
          {/* Mobile Card View for Table Mode */}
          <div className="block md:hidden space-y-3">
            {sortedTrips.map(trip => (
              <div
                key={trip.id}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-4"
              >
                {/* Header: Title and Status */}
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-slate-900 truncate">
                      {trip.title || 'Untitled Trip'}
                    </h3>
                    {(trip.start_date || trip.end_date) && (
                      <p className="text-sm text-slate-500 mt-0.5">
                        {formatDate(trip.start_date)}{trip.end_date && ` - ${formatDate(trip.end_date)}`}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {trip.is_confidential && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-300"
                        title="This trip is confidential"
                      >
                        <Lock className="w-3 h-3" />
                      </span>
                    )}
                    {trip.archived && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                        <Archive className="w-3 h-3" />
                        Archived
                      </span>
                    )}
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold border ${getStatusColor(trip?.status || 'planning')}`}>
                      {(trip?.status || 'planning').replace('_', ' ')}
                    </span>
                  </div>
                </div>

                {/* Middle: Executives / Speakers */}
                {getTripPeopleNames(trip).length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {getTripPeopleNames(trip).map((name, idx) => (
                      <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">
                        {name}
                      </span>
                    ))}
                  </div>
                )}

                {/* Footer: Actions */}
                <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                  <span className="text-xs text-slate-400">
                    by {getCreatorName(trip.created_by)}
                  </span>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => navigate(`/trips?id=${trip.id}`)}
                      className="text-indigo-600 hover:text-indigo-900 font-medium text-sm"
                    >
                      Open
                    </button>
                    <button
                      onClick={() => onArchiveTrip(trip.id, trip.archived)}
                      className="text-amber-600 hover:text-amber-900 font-medium text-sm"
                      title={trip.archived ? 'Unarchive' : 'Archive'}
                    >
                      {trip.archived ? 'Unarchive' : 'Archive'}
                    </button>
                    <button
                      onClick={() => onDeleteTrip(trip.id)}
                      className="text-red-600 hover:text-red-900 font-medium text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {sortedTrips.length === 0 && (
              <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                </svg>
                <h3 className="text-lg font-semibold text-slate-600 mb-2">No Trips Found</h3>
                <p className="text-slate-500 mb-4">Create your first trip to get started</p>
              </div>
            )}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    {[
                      { key: 'title', label: 'Trip Title' },
                      { key: 'dates', label: 'Dates' },
                      { key: 'executives', label: labels.executives },
                      { key: 'created_by', label: 'Created By' },
                      { key: 'status', label: 'Status' },
                    ].map(col => (
                      <th
                        key={col.key}
                        className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider cursor-pointer select-none hover:bg-slate-100 transition-colors"
                        onClick={() => handleSort(col.key)}
                      >
                        <span className="inline-flex items-center gap-1">
                          {col.label}
                          {sortKey === col.key ? (
                            sortDirection === 'asc' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronsUpDown className="w-3.5 h-3.5 text-slate-400" />
                          )}
                        </span>
                      </th>
                    ))}
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {sortedTrips.map(trip => {
                    const usersWithAccess = getUsersWithAccess(trip);
                    return (
                    <tr key={trip.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 lg:px-6 py-4">
                        <div className="text-sm font-medium text-slate-900 truncate max-w-[200px]">
                          {trip.title || 'Untitled Trip'}
                        </div>
                      </td>
                      <td className="px-4 lg:px-6 py-4 text-sm text-slate-600">
                        <div>{formatDate(trip.start_date) || '—'}</div>
                        {trip.end_date && formatDate(trip.end_date) !== formatDate(trip.start_date) && (
                          <div className="text-xs text-slate-400">to {formatDate(trip.end_date)}</div>
                        )}
                      </td>
                      <td className="px-4 lg:px-6 py-4">
                        {getTripPeopleNames(trip).length > 0 ? (
                          <div className="flex flex-wrap gap-1 max-w-[180px]">
                            {getTripPeopleNames(trip).slice(0, 2).map((name, idx) => (
                              <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">
                                {name}
                              </span>
                            ))}
                            {getTripPeopleNames(trip).length > 2 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">
                                +{getTripPeopleNames(trip).length - 2}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 italic text-sm">None</span>
                        )}
                      </td>
                      <td className="px-4 lg:px-6 py-4">
                        <div className="relative group">
                          <span className="text-sm text-slate-700 cursor-default">
                            {getCreatorName(trip.created_by)}
                          </span>
                          {usersWithAccess.length > 0 && (
                            <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover:block bg-slate-800 text-white text-xs rounded-lg shadow-lg p-3 min-w-[200px] max-w-[300px]">
                              <div className="font-semibold mb-2 text-slate-200">Others with access:</div>
                              <div className="space-y-1.5">
                                {usersWithAccess.map((u, idx) => (
                                  <div key={idx} className="flex justify-between gap-2">
                                    <span className="truncate">{u.name}</span>
                                    <span className="text-slate-400 text-xs whitespace-nowrap">{u.accessType}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 lg:px-6 py-4">
                        <div className="flex items-center gap-1.5">
                          {trip.is_confidential && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-300"
                              title="This trip is confidential"
                            >
                              <Lock className="w-3 h-3" />
                            </span>
                          )}
                          {trip.archived && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                              <Archive className="w-3 h-3" />
                              Archived
                            </span>
                          )}
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold border ${getStatusColor(trip?.status || 'planning')}`}>
                            {(trip?.status || 'planning').replace('_', ' ')}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-right text-sm">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate(`/trips?id=${trip.id}`)}
                            className="text-indigo-600 hover:text-indigo-900 font-medium"
                          >
                            Open
                          </button>
                          <button
                            onClick={() => onArchiveTrip(trip.id, trip.archived)}
                            className="text-amber-600 hover:text-amber-900 font-medium"
                            title={trip.archived ? 'Unarchive' : 'Archive'}
                          >
                            {trip.archived ? 'Unarchive' : 'Archive'}
                          </button>
                          <button
                            onClick={() => onDeleteTrip(trip.id)}
                            className="text-red-600 hover:text-red-900 font-medium"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>

            {sortedTrips.length === 0 && (
              <div className="text-center py-12">
                <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                </svg>
                <h3 className="text-lg font-semibold text-slate-600 mb-2">No Trips Found</h3>
                <p className="text-slate-500 mb-4">Create your first trip to get started</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Icon/Card View */}
      {!loading && viewMode === 'icon' && (
        <div className="grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {sortedTrips.map(trip => (
            <div
              key={trip.id}
              className="group bg-white/90 backdrop-blur-lg border-0 shadow-lg sm:shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden rounded-2xl sm:rounded-3xl h-full flex flex-col hover:-translate-y-1 active:scale-[0.99] sm:active:scale-100"
              style={{
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)'
              }}
            >
              {/* Card Header */}
              <div className="pb-3 sm:pb-4 flex-shrink-0 px-4 sm:px-6 pt-4 sm:pt-6">
                <div className="flex items-start justify-between mb-2 sm:mb-3">
                  {/* Status & Purpose Badges */}
                  <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {trip.is_confidential && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 sm:py-1 rounded-md text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-300"
                        title="This trip is confidential"
                      >
                        <Lock className="w-3 h-3" />
                        Confidential
                      </span>
                    )}
                    {trip.archived && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 sm:py-1 rounded-md text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                        <Archive className="w-3 h-3" />
                        Archived
                      </span>
                    )}
                    <span className={`inline-flex items-center px-2 py-0.5 sm:py-1 rounded-md text-xs font-semibold border ${getStatusColor(trip?.status || 'planning')}`}>
                      {(trip?.status || 'planning').replace('_', ' ')}
                    </span>
                    {trip.trip_purpose && (
                      <span className={`inline-flex items-center px-2 py-0.5 sm:py-1 rounded-md text-xs font-semibold border ${purposeColors[trip.trip_purpose] || purposeColors.other}`}>
                        {trip.trip_purpose.replace(/_/g, ' ')}
                      </span>
                    )}
                  </div>

                  {/* Todo Count Badge */}
                  {getTodoCountForTrip(trip.id) > 0 && (
                    <Link
                      to={`/todos?trip=${trip.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-1 bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs font-semibold hover:bg-amber-200 transition-colors cursor-pointer touch-target"
                    >
                      <CheckSquare className="w-3 h-3" />
                      {getTodoCountForTrip(trip.id)}
                    </Link>
                  )}
                </div>

                {/* Trip Title */}
                <h3 className="text-lg sm:text-xl font-bold text-slate-800 group-hover:text-violet-700 transition-colors leading-tight mb-1 sm:mb-2 line-clamp-2">
                  {trip.title || trip.destination || 'Untitled Trip'}
                </h3>
              </div>

              {/* Card Content */}
              <div className="px-4 sm:px-6 pb-4 sm:pb-6 flex flex-col flex-grow">
                {/* Trip Details */}
                <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6 flex-grow">
                  {/* Destination */}
                  {trip.destination && (
                    <div className="flex items-center gap-3 text-sm text-slate-600">
                      <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="font-medium">{trip.destination}</span>
                    </div>
                  )}

                  {/* Date Range */}
                  {(trip.start_date || trip.end_date) && (
                    <div className="flex items-center gap-3 text-sm text-slate-600">
                      <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>
                        {formatDate(trip.start_date)}{trip.end_date && ` - ${formatDate(trip.end_date)}`}
                      </span>
                    </div>
                  )}

                  {/* Executives / Speakers */}
                  {getTripPeopleNames(trip).length > 0 && (
                    <div className="flex items-start gap-3 text-sm text-slate-600">
                      <svg className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      <div className="flex-1">
                        <div className="flex flex-wrap gap-1">
                          {getTripPeopleNames(trip).map((name, idx) => (
                            <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-800 font-medium">
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 mt-auto">
                  <button
                    onClick={() => navigate(`/trips?id=${trip.id}`)}
                    className="flex-1 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white transition-all duration-300 rounded-xl sm:rounded-2xl h-11 sm:h-12 text-sm sm:text-base font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 active:shadow-lg touch-target"
                  >
                    View Details
                  </button>
                  <button
                    onClick={() => onArchiveTrip(trip.id, trip.archived)}
                    className="flex items-center justify-center gap-1 bg-amber-50 hover:bg-amber-100 text-amber-600 px-3 sm:px-4 rounded-xl sm:rounded-2xl transition-colors touch-target"
                    title={trip.archived ? 'Unarchive' : 'Archive'}
                  >
                    {trip.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => onDeleteTrip(trip.id)}
                    className="flex items-center justify-center gap-1 bg-red-50 hover:bg-red-100 text-red-600 px-3 sm:px-4 rounded-xl sm:rounded-2xl transition-colors touch-target"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}

          {sortedTrips.length === 0 && (
            <div className="col-span-full text-center py-12">
              <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
              </svg>
              <h3 className="text-lg font-semibold text-slate-600 mb-2">No Trips Found</h3>
              <p className="text-slate-500 mb-4">Create your first trip to get started</p>
              <button
                onClick={onCreateTrip}
                className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white px-6 py-3 rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                New Trip
              </button>
            </div>
          )}
        </div>
      )}

      {/* Create Trip Modal */}
      {showCreateModal && (
        <CreateTripModal2
          onClose={() => setShowCreateModal(false)}
          onTripCreated={handleTripCreated}
          executives={executives}
        />
      )}
    </div>
  );
};

export default TripManagement;
